/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ActionButton, ActionButtonType, Callout, ThirdPartyLogo, StepCard } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { ActionTemplateResource, ActionTemplateSearchResource } from "@octopusdeploy/octopus-server-client";
import { ActionHandlerCategory } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { groupBy } from "lodash";
import * as React from "react";
import ActionTemplateCategory from "~/components/ActionTemplates/ActionTemplateCategory";
import type { Result } from "~/components/ActionTemplates/filterActionTemplates";
import filterActionTemplates from "~/components/ActionTemplates/filterActionTemplates";
import FeatureToggle, { Feature } from "~/components/FeatureToggle/FeatureToggle";
import Markdown from "~/components/Markdown";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import Section from "~/components/Section";
import TransitionAnimation from "~/components/TransitionAnimation/TransitionAnimation";
import { resolveUrl } from "~/utils/resolveUrl";
import Dialog from "../Dialog/Dialog";
import InternalRedirect from "../Navigation/InternalRedirect/InternalRedirect";
import ActionTemplateCardList from "./ActionTemplateCardList";
import CommunityTemplateInstallation from "./CommunityTemplateInstallation";
import styles from "./styles.module.less";
import "animate.css";
type CommunityActionTemplateListInternalProps = {
    spaceId: string | null;
    templates: ActionTemplateSearchResource[];
    filter?: string;
    installationActionName: string;
    activeCategory?: ActionHandlerCategory | undefined;
    onPostSelectionUrlRequested: (template: {
        Type: string;
        Id: string | null;
        SpaceId: string | null;
        CommunityActionTemplateId: string | null;
    }) => LinkHref;
    onDetailsUrlRequested: (template: ActionTemplateSearchResource) => {
        href: LinkHref;
        external: boolean;
    };
    openInNewTab: (url: LinkHref) => void;
    isProducerTemplateProcess?: boolean;
};
interface CommunityActionTemplateListState {
    categories?: Array<{
        name: string;
        templates: ActionTemplateSearchResource[];
    }>;
    categoryTemplates?: ActionTemplateSearchResource[];
    openDialogTemplateId?: string;
    redirectTo?: LinkHref;
}
class CommunityActionTemplateListInternal extends React.Component<CommunityActionTemplateListInternalProps, CommunityActionTemplateListState> {
    private categoriesElement: HTMLOListElement | null = undefined!;
    private categoryTemplatesElement: HTMLElement | null = undefined!;
    constructor(props: CommunityActionTemplateListInternalProps) {
        super(props);
        this.state = {
            categories: this.categoriseTemplates(props.templates),
            categoryTemplates: [],
        };
    }
    categoriseTemplates(templates: ActionTemplateSearchResource[]) {
        const categorised = groupBy(templates, (t) => t.Categories && t.Categories.length > 0 && t.Categories[0]); // Community steps have a single category (other than the "Community" parent category).
        return Object.keys(categorised)
            .sort((category1, category2) => category1.toLowerCase().localeCompare(category2.toLowerCase()))
            .map((categoryName) => ({ name: categoryName, templates: categorised[categoryName] }));
    }
    showCategoryTemplates(templates: ActionTemplateSearchResource[]) {
        // We are setting min-hight here to avoid "screen jumping" when the user switches from categories to templates.
        // Ideally this would be handled by a CSS transform but it did not work.
        this.categoryTemplatesElement!.style.minHeight = this.categoriesElement!.clientHeight + "px";
        this.setState({ categoryTemplates: templates, categories: [] });
    }
    showCategories = () => {
        this.setState({ categoryTemplates: [], categories: this.categoriseTemplates(this.props.templates) });
    };
    showAllTemplates = () => {
        this.setState({ categoryTemplates: this.props.templates, categories: [] });
    };
    isInCategoryMode() {
        return this.state.categoryTemplates!.length === 0 && this.state.categories!.length > 0;
    }
    isInCategoryTemplatesMode() {
        return this.state.categoryTemplates!.length > 0 && this.state.categories!.length === 0;
    }
    calculateNumberOfTemplatesShown(isFiltering: boolean, filteredResults: Result[]): number {
        if (isFiltering) {
            return filteredResults.length;
        }
        return this.state.categoryTemplates?.length ? this.state.categoryTemplates!.length : this.props.templates.length;
    }
    //eslint-disable-next-line react/no-unsafe
    UNSAFE_componentWillReceiveProps(nextProps: CommunityActionTemplateListInternalProps) {
        if (nextProps.filter !== this.props.filter) {
            const nextState: Partial<CommunityActionTemplateListState> = { categories: this.categoriseTemplates(nextProps.templates) };
            if (!nextProps.filter) {
                nextState.categoryTemplates = [];
            }
            this.setState(nextState);
        }
    }
    render() {
        if (this.state.redirectTo) {
            return <InternalRedirect to={this.state.redirectTo} push={true}/>;
        }
        const isFiltering = !!this.props.filter || this.props.activeCategory;
        const filter = this.props.filter ? this.props.filter : this.props.activeCategory?.toLocaleLowerCase();
        const filteredResults = filterActionTemplates(this.props.templates, filter!);
        const numberOfTemplatesShown = this.calculateNumberOfTemplatesShown(!!isFiltering, filteredResults);
        const sectionHeader = !filteredResults?.length && this.props.activeCategory ? "" : `Community Contributed Step Templates (${numberOfTemplatesShown})`;
        return (<div className={styles.container}>
                <Section sectionHeader={sectionHeader} className={styles.stepTemplatesHeader}>
                    <FeatureToggle feature={Feature.CommunityActionTemplates} enabled={false}>
                        <Callout type={"information"} title={"Community library is disabled"}>
                            If you want to take advantage of hundreds of great templates developed by the members of our community then please <InternalLink to={links.featuresPage.generateUrl()}>enable the integration feature.</InternalLink>
                        </Callout>
                    </FeatureToggle>
                    <FeatureToggle feature={Feature.CommunityActionTemplates}>
                        {this.props.templates.length === 0 && !this.props.filter && (<Callout type={"information"} title={"No community step templates available"}>
                                Community library integration is enabled but there aren't any community step templates available. Review the community step templates <InternalLink to={links.featuresPage.generateUrl()}>feature toggle</InternalLink>
                                &nbsp; for more information.
                            </Callout>)}
                    </FeatureToggle>
                </Section>
                {!isFiltering ? (<Section bodyClassName={styles.stepTemplatesSection}>
                        <div className={styles.actions}>
                            {this.isInCategoryMode() && <ActionButton label="Show all" type={ActionButtonType.Secondary} onClick={this.showAllTemplates}/>}
                            {this.isInCategoryTemplatesMode() && <ActionButton label={this.state.categoryTemplates?.length !== this.props.templates?.length ? "Back" : "Show less"} type={ActionButtonType.Secondary} onClick={this.showCategories}/>}
                        </div>
                        <div className={styles.groupingContainer}>
                            {this.state.categories && this.state.categories.length > 0 && (<ol className={styles.categories} ref={(element) => (this.categoriesElement = element)}>
                                    <div>
                                        {this.state.categories.map((category) => {
                        return this.withAnimation(category.name, <ActionTemplateCategory category={ActionHandlerCategory.CommunitySubCategory} name={category.name} active={false} templates={category.templates} onCategorySelected={() => this.showCategoryTemplates(category.templates)}/>);
                    })}
                                    </div>
                                </ol>)}
                            <div className={styles.categoryTemplates} ref={(element) => (this.categoryTemplatesElement = element)}>
                                <ActionTemplateCardList>{this.state.categoryTemplates!.map((template) => this.renderSingle(template))}</ActionTemplateCardList>
                            </div>
                        </div>
                    </Section>) : (this.filteredTemplates(filteredResults))}
            </div>);
    }
    private filteredTemplates(filteredResults: ActionTemplateSearchResource[]) {
        if (!filteredResults?.length) {
            return this.props.filter ? <Section bodyClassName={styles.stepTemplatesNoResult}>No results found</Section> : <></>;
        }
        return (<TransitionAnimation key="filteredSteps">
                <ActionTemplateCardList>{filteredResults.map((template) => this.renderSingle(template))}</ActionTemplateCardList>
            </TransitionAnimation>);
    }
    private renderSingle(template: ActionTemplateSearchResource) {
        const urlDetails = this.props.onDetailsUrlRequested(template);
        return (<div key={template.Type + template.Id}>
                <StepCard heading={template.Name} subHeading={`By ${template.Author}`} logo={<ThirdPartyLogo url={resolveUrl(template.Links.Logo)} size={space[40]} isSquare={true}/>} chipLabel="Community Contributed" description={<Markdown markup={template.Description} noMargins mutedTextColor/>} dotpoints={template.Features} primaryAction={{
                label: this.props.installationActionName,
                onClick: this.props.isProducerTemplateProcess
                    ? () => {
                        const url = this.props.onPostSelectionUrlRequested({ Type: template.Type, Id: template.Id, SpaceId: template.SpaceId, CommunityActionTemplateId: template.CommunityActionTemplateId });
                        this.setState({ redirectTo: url });
                    }
                    : () => this.setState({ openDialogTemplateId: template.Id }),
            }} secondaryAction={{
                label: "More Info",
                ...urlDetails,
            }}/>
                {this.props.spaceId === null ? null : (<Dialog open={this.state.openDialogTemplateId === template.Id}>
                        <CommunityTemplateInstallation spaceId={this.props.spaceId} communityTemplateId={template.Id} title={this.props.installationActionName} detailsLink={urlDetails.href} saveDone={this.installationSucceeded} onCancelClick={() => this.setState({ openDialogTemplateId: undefined })}/>
                    </Dialog>)}
            </div>);
    }
    private withAnimation(key: string, content: React.ReactNode) {
        return (<div key={key} className="animate__animated animate__zoomIn">
                {content}
            </div>);
    }
    private installationSucceeded = (actionTemplate: ActionTemplateResource) => {
        const url = this.props.onPostSelectionUrlRequested!({ Type: actionTemplate.ActionType, Id: actionTemplate.Id, SpaceId: actionTemplate.SpaceId, CommunityActionTemplateId: actionTemplate.CommunityActionTemplateId });
        this.setState({ redirectTo: url });
    };
    static displayName = "CommunityActionTemplateListInternal";
}
type CommunityActionTemplateListProps = Omit<CommunityActionTemplateListInternalProps, "openInNewTab">;
export function CommunityActionTemplateList(props: CommunityActionTemplateListProps) {
    const navigation = useSpaceAwareNavigation();
    return <CommunityActionTemplateListInternal {...props} openInNewTab={navigation.open}/>;
}
